import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Activity } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import { Button } from "@/components/ui/button";
import { verifyIsAdmin } from "@/lib/admin.functions";
import { AuditLogPanel } from "@/components/AuditLogPanel";
import { PendingGoblinPanel } from "@/components/PendingGoblinPanel";

export const Route = createFileRoute("/_app/admin/activity")({
  component: AdminActivityPage,
  head: () => ({ meta: [{ title: "Admin activity — Bracket Challenge" }] }),
});

function AdminActivityPage() {
  const { session, loading } = useAuth();
  const [serverIsAdmin, setServerIsAdmin] = useState<boolean | null>(null);
  const verifyAdminFn = useAuthedServerFn(verifyIsAdmin);

  useEffect(() => {
    if (!session) {
      setServerIsAdmin(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await verifyAdminFn();
        if (!cancelled) setServerIsAdmin(res.isAdmin);
      } catch {
        if (!cancelled) setServerIsAdmin(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  if (loading || (session && serverIsAdmin === null)) {
    return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  }
  if (!serverIsAdmin) {
    return (
      <main className="max-w-md mx-auto px-4 py-16 text-center">
        <h1 className="font-display text-2xl font-bold text-foreground mb-2">
          Admins only
        </h1>
        <Button asChild variant="outline">
          <Link to="/home">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </Button>
      </main>
    );
  }

  return (
    <main className="max-w-5xl mx-auto px-4 py-8">
      <header className="mb-6">
        <Link
          to="/admin"
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2"
        >
          <ArrowLeft className="w-3 h-3" /> Back to admin
        </Link>
        <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-2">
          <Activity className="w-6 h-6 text-primary" />
          Admin activity
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          All operational admin actions across every league. GDPR erasure
          requests are logged anonymously without identifying info about the
          deleted user.
        </p>
      </header>

      <div className="mb-6">
        <PendingGoblinPanel />
      </div>

      <AuditLogPanel
        title="Global activity"
        description="Filter by action, time range, actor, or target. Export to CSV for offline analysis."
      />
    </main>
  );
}

